"use client";

import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/hooks/useAppSelector";
import { fetchProducts } from "@/store/slices/homeSlice";
import { Backdrop, Button, CircularProgress } from "@mui/material";
import Image from "next/image";
import { clearFilters, setSelectedBrand } from "@/store/slices/stepSlice";
import { trackEvent } from "@/lib/gtag";
import clsx from "clsx";
import { Manufacturer } from "@/types/home";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

const IMAGE_BASE = process.env.NEXT_PUBLIC_IMAGE_BASE;

const BrandSelection: React.FC = () => {
  const dispatch = useAppDispatch();
  const { manufacturers } = useAppSelector((state) => state.home);
  const { selectedBrand } = useAppSelector((state) => state.steps);
  const [selectedTab, setSelectedTab] = useState<"domestic" | "premium">(
    "domestic"
  );
  const [openprogress, setOpenProgress] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null!);

  const mergeOthers = (category: "domestic" | "premium"): Manufacturer[] => {
    const merged = [
      ...(manufacturers?.others ?? []),
      ...(manufacturers?.[category] ?? []),
    ];
    return merged;
  };

  useEffect(() => {
    const brands = mergeOthers("domestic");
    if (brands?.length && !selectedBrand) {
      setOpenProgress(true);
      dispatch(setSelectedBrand("MASTER"));
      dispatch(fetchProducts("MASTER"))?.finally(() => setOpenProgress(false));
    }
    else{
      dispatch(fetchProducts(selectedBrand || "MASTER"))?.finally(() => setOpenProgress(false));
    }
  }, [dispatch, manufacturers]);

  const fetchProduct = (brand: string | null) => {
    dispatch(clearFilters());
    dispatch(setSelectedBrand(brand || "MASTER"));
    dispatch(fetchProducts(brand || "MASTER"));
    trackEvent({
      action: "brand_click",
      category: "brand_selection",
      label: `Brand Selected: ${brand || "MASTER"}`,
    });
  };

  const scrollHorizontally = (direction: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === "left" ? -150 : 150,
        behavior: "smooth",
      });
    }
  };

  const renderBrands = () => (
    <div className="relative flex items-center">
      <button
        onClick={() => scrollHorizontally("left")}
        className="absolute left-0 z-10 bg-white-1 shadow-md rounded-full p-1"
      >
        <ChevronLeft fontSize="small" className="text-black-1" />
      </button>

      <div
        ref={scrollRef}
        className="mx-10 flex space-x-4 overflow-x-auto hide-scrollbar scroll-smooth py-2"
      >
        {mergeOthers(selectedTab).map((brand) => {
          const isSelected =
            selectedBrand?.toLowerCase() ===
            brand.manufacture_name.toLowerCase();

          return (
            <Button
              key={brand.manufacture_code}
              variant="outlined"
              onClick={() => fetchProduct(brand.manufacture_name)}
              style={{
                backgroundColor: isSelected ? "var(--color-blue)" : "#f1f1f1",
                color: isSelected ? "white" : "var(--color-blue)",
                border: isSelected ? "1px solid var(--color-blue)" : "none",
                borderRadius: "20px",
                minWidth: "80px",
                height: "32px",
                fontSize: "13px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "5px",
              }}
            >
              {openprogress && isSelected ? (
                <CircularProgress size={18} sx={{ color: "white" }} />
              ) : (
                <Image
                  unoptimized
                  quality={60}
                  src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${IMAGE_BASE}_MANUFACTURE_IMAGE/${brand.manufacture_images}`}
                  alt={brand.manufacture_name}
                  width={70}
                  height={22}
                  style={{
                    width: "80px",
                    height: "32px",
                    objectFit: "contain",
                    padding: "2px",
                    background: isSelected ? "white" : "transparent",
                    borderRadius: "20px",
                  }}
                />
              )}
            </Button>
          );
        })}
      </div>

      <button
        onClick={() => scrollHorizontally("right")}
        className="absolute right-0 z-10 bg-white-1 shadow-md rounded-full p-1"
      >
        <ChevronRight fontSize="small" className="text-black-1" />
      </button>
    </div>
  );

  return (
    <div className="w-full px-2">
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openprogress}
      >
        <CircularProgress size={20} sx={{ color: "var(--color-blue)" }} />
      </Backdrop>

      {/* Tabs */}
      <div className="flex mb-2 ">
        {["domestic", "premium"].map((tab) => (
          <button
            key={tab}
            onClick={() => setSelectedTab(tab as "domestic" | "premium")}
            className={clsx(
              "px-4 py-2 text-sm font-semibold capitalize relative",
              selectedTab === tab ? "text-green" : "text-gray-500"
            )}
          >
            {tab}
            {selectedTab === tab && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-green rounded-full" />
            )}
          </button>
        ))}
      </div>

      {/* Brands */}
      {manufacturers && renderBrands()}
    </div>
  );
};

export default BrandSelection;
